/**
 * Shared helpers for working with ISO date strings used by the reports UI.
 */

const formatToIsoDate = (date) => {
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const getISODateWithOffset = (offsetDays = 0) => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + offsetDays);
  return formatToIsoDate(date);
};

export const getTodayISODate = () => getISODateWithOffset(0);

export const getYesterdayISODate = () => getISODateWithOffset(-1);

export const clampDateToToday = (value) => {
  if (!value) return '';
  const today = getTodayISODate();
  return value > today ? today : value;
};

export const getLastWeekStartISODate = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);

  const dayOfWeek = date.getDay(); // 0 (Sun) - 6 (Sat)
  const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

  // Move to current week's Monday, then go back 7 days to reach last week's start
  date.setDate(date.getDate() - daysSinceMonday - 7);
  return formatToIsoDate(date);
};

export const getWeekStartISODate = (value) => {
  if (!value) return '';

  const date = new Date(value);
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return '';
  }

  date.setHours(0, 0, 0, 0);
  const dayOfWeek = date.getDay();
  const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  date.setDate(date.getDate() - daysSinceMonday);
  return formatToIsoDate(date);
};

const formatMonthFromDate = (date) => {
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return '';
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

export const getCurrentMonthInputValue = () => formatMonthFromDate(new Date());

export const getPreviousMonthInputValue = () => {
  const date = new Date();
  date.setDate(1);
  date.setMonth(date.getMonth() - 1);
  return formatMonthFromDate(date);
};

export const clampMonthToCurrent = (value) => {
  if (!value) return '';
  const current = getCurrentMonthInputValue();
  return value > current ? current : value;
};

/**
 * Formats date + time for telematics report API (UTC wall clock, no Z suffix).
 * @param {string} dateStr - YYYY-MM-DD
 * @param {string} timeStr - HH:mm
 * @returns {string|null}
 */
export const formatReportWindowTimestamp = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return null;
  const [hh = '00', mm = '00'] = String(timeStr).split(':');
  return `${dateStr}T${hh.padStart(2, '0')}:${mm.padStart(2, '0')}:00`;
};

